import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  CalendarDays,
  CheckCircle2,
  Clock3,
  MapPin,
  Mic2,
  Play,
  Radio,
  Send,
  ShieldCheck,
  Sparkles,
  Users,
  Video,
} from 'lucide-react';
import SEO from '@/components/SEO';
import { useArtistAuth } from '@/context/ArtistAuthContext';
import { useCollaboratorAuth } from '@/context/CollaboratorAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const emptyForm = {
  title: '',
  status: 'upcoming',
  audition_date: '',
  art_form: '',
  city: '',
  venue: '',
  description: '',
  rules: '',
  youtube_url: '',
  image_url: '',
};

const tabs = [
  { id: 'live', label: 'Live now', icon: Radio },
  { id: 'upcoming', label: 'Upcoming', icon: CalendarDays },
  { id: 'past', label: 'Past', icon: Clock3 },
];

const statusStyles = {
  live: 'bg-red-50 text-red-700 ring-red-200',
  upcoming: 'bg-orange-50 text-[#C45A00] ring-orange-200',
  past: 'bg-slate-100 text-slate-700 ring-slate-200',
};

const youtubeEmbedUrl = (url = '') => {
  const value = String(url || '').trim();
  const idMatch = value.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/live\/)([A-Za-z0-9_-]{6,})/);
  return idMatch ? `https://www.youtube.com/embed/${idMatch[1]}` : '';
};

const metaLine = (audition) => {
  const parts = [audition.venue, audition.city].filter(Boolean);
  return parts.length ? parts.join(', ') : 'Online or venue to be announced';
};

const AuditionMedia = ({ audition, large = false }) => {
  const embedUrl = youtubeEmbedUrl(audition.youtube_url);
  const heightClass = large ? 'min-h-[300px] sm:min-h-[420px]' : 'min-h-[220px] sm:min-h-[260px]';

  if (embedUrl) {
    return (
      <iframe
        title={audition.title}
        src={embedUrl}
        className={`aspect-video w-full bg-slate-950 ${heightClass}`}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    );
  }

  if (audition.image_url) {
    return (
      <img
        src={audition.image_url}
        alt={audition.title}
        className={`w-full object-cover ${large ? 'h-[300px] sm:h-[420px]' : 'h-[240px]'}`}
      />
    );
  }

  return (
    <div className={`flex w-full items-center justify-center bg-[#07111F] text-white ${large ? 'h-[300px] sm:h-[420px]' : 'h-[240px]'}`}>
      <div className="text-center">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-white/10 ring-1 ring-white/20">
          <Mic2 className="h-8 w-8 text-[#FFB347]" />
        </span>
        <p className="mt-4 text-sm font-extrabold uppercase tracking-[0.3em] text-[#FFB347]">KalaManch Audition</p>
      </div>
    </div>
  );
};

const LiveAuditions = () => {
  const { artist, isAuthenticated: artistLoggedIn } = useArtistAuth();
  const { collaborator, isAuthenticated: collaboratorLoggedIn } = useCollaboratorAuth();
  const [activeTab, setActiveTab] = useState('live');
  const [auditions, setAuditions] = useState([]);
  const [artistInvites, setArtistInvites] = useState([]);
  const [posterInvites, setPosterInvites] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const loadAuditions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/live-auditions`);
      setAuditions(response.data.auditions || []);
    } catch (error) {
      setAuditions([]);
    } finally {
      setLoading(false);
    }
  };

  const loadArtistInvites = useCallback(async () => {
    if (!artistLoggedIn) {
      setArtistInvites([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/artist-auth/live-auditions/invites`, { withCredentials: true });
      setArtistInvites(response.data.invites || []);
    } catch (error) {
      setArtistInvites([]);
    }
  }, [artistLoggedIn]);

  const loadPosterInvites = useCallback(async () => {
    if (!collaboratorLoggedIn) {
      setPosterInvites([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/collaborator-auth/live-auditions/invites`, { withCredentials: true });
      setPosterInvites(response.data.invites || []);
    } catch (error) {
      setPosterInvites([]);
    }
  }, [collaboratorLoggedIn]);

  useEffect(() => {
    loadAuditions();
  }, []);

  useEffect(() => {
    loadArtistInvites();
  }, [loadArtistInvites]);

  useEffect(() => {
    loadPosterInvites();
  }, [loadPosterInvites]);

  const grouped = useMemo(() => tabs.reduce((acc, tab) => ({
    ...acc,
    [tab.id]: auditions.filter((audition) => (audition.status || 'upcoming') === tab.id),
  }), {}), [auditions]);

  const featuredAudition = grouped.live?.[0] || grouped.upcoming?.[0] || auditions[0];
  const visibleAuditions = grouped[activeTab] || [];
  const totalPublished = auditions.length;

  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const submitAudition = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      const response = await axios.post(`${API}/live-auditions`, form, { withCredentials: true });
      setForm(emptyForm);
      setMessage(response.data.message || 'Live audition submitted for admin review.');
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Unable to submit live audition.');
    } finally {
      setSaving(false);
    }
  };

  const respondToInvite = async (invite, action) => {
    setMessage('');
    try {
      await axios.post(
        `${API}/artist-auth/live-auditions/${invite.audition_id}/invites/${invite.artist_id}/action`,
        { action },
        { withCredentials: true },
      );
      setMessage(action === 'accept' ? 'Audition invite accepted.' : 'Audition invite declined.');
      await loadArtistInvites();
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Unable to update audition invite.');
    }
  };

  const updatePosterInvite = async (invite, status) => {
    setMessage('');
    try {
      await axios.put(
        `${API}/collaborator-auth/live-auditions/${invite.audition_id}/invites/${invite.artist_id}`,
        { status, public_visible: status === 'selected' ? Boolean(invite.public_visible) : false },
        { withCredentials: true },
      );
      setMessage('Audition participant status updated.');
      await loadPosterInvites();
      await loadAuditions();
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Unable to update participant status.');
    }
  };

  const togglePosterPublic = async (invite) => {
    setMessage('');
    try {
      await axios.put(
        `${API}/collaborator-auth/live-auditions/${invite.audition_id}/invites/${invite.artist_id}`,
        { status: invite.status || 'invited', public_visible: !invite.public_visible },
        { withCredentials: true },
      );
      setMessage('Participant public visibility updated.');
      await loadPosterInvites();
      await loadAuditions();
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Unable to publish participant.');
    }
  };

  return (
    <main className="min-h-screen bg-[#F6F8FB] pt-24 text-slate-950">
      <SEO
        path="/live-auditions"
        title="KalaManch Live Auditions"
        description="Watch live and upcoming KalaManch auditions posted by collaborators and admin for Indian artists and performers."
        keywords="KalaManch live auditions, artist auditions India, YouTube live auditions, performer auditions"
      />

      <section className="bg-[#07111F] text-white">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-10 sm:px-6 lg:grid-cols-[0.92fr_1.08fr] lg:px-8 lg:py-14">
          <div className="flex flex-col justify-center">
            <div className="inline-flex w-fit items-center gap-2 rounded-full border border-white/15 bg-white/8 px-4 py-2 text-xs font-extrabold uppercase tracking-[0.22em] text-[#FFB347]">
              <Radio className="h-4 w-4" />
              Live Auditions
            </div>
            <h1 className="mt-6 max-w-3xl text-4xl font-black leading-[1.02] sm:text-5xl lg:text-6xl">
              Watch. Apply. Get discovered live.
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-8 text-slate-200 sm:text-lg">
              A dedicated stage for KalaManch auditions, creative calls, and live selection rounds. Collaborators post opportunities, admin approves them, and artists get a sharper way to prepare.
            </p>

            <div className="mt-7 grid max-w-2xl gap-3 sm:grid-cols-3">
              <div className="rounded-2xl border border-white/10 bg-white/8 p-4">
                <p className="text-3xl font-black text-white">{grouped.live?.length || 0}</p>
                <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-300">Live now</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/8 p-4">
                <p className="text-3xl font-black text-white">{grouped.upcoming?.length || 0}</p>
                <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-300">Upcoming</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/8 p-4">
                <p className="text-3xl font-black text-white">{totalPublished}</p>
                <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-300">Published</p>
              </div>
            </div>
          </div>

          <div className="overflow-hidden rounded-[2rem] border border-white/10 bg-white shadow-2xl shadow-black/30">
            {featuredAudition ? (
              <>
                <AuditionMedia audition={featuredAudition} large />
                <div className="bg-white p-5 text-slate-950 sm:p-6">
                  <div className="flex flex-wrap gap-2">
                    <span className={`rounded-full px-3 py-1 text-xs font-extrabold uppercase tracking-[0.12em] ring-1 ${statusStyles[featuredAudition.status] || statusStyles.upcoming}`}>
                      {featuredAudition.status || 'upcoming'}
                    </span>
                    {featuredAudition.art_form && (
                      <span className="rounded-full bg-purple-50 px-3 py-1 text-xs font-extrabold text-[#4A148C]">{featuredAudition.art_form}</span>
                    )}
                  </div>
                  <h2 className="mt-3 text-2xl font-black leading-tight sm:text-3xl">{featuredAudition.title}</h2>
                  <p className="mt-2 flex items-start gap-2 text-sm font-semibold text-slate-600">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#FF6F00]" />
                    {metaLine(featuredAudition)}
                  </p>
                </div>
              </>
            ) : (
              <div className="p-8 sm:p-10">
                <div className="rounded-[1.5rem] border border-dashed border-orange-200 bg-orange-50 p-8 text-center">
                  <Video className="mx-auto h-12 w-12 text-[#FF6F00]" />
                  <h2 className="mt-4 text-2xl font-black text-slate-950">First live audition coming soon.</h2>
                  <p className="mt-2 text-sm leading-6 text-slate-600">Admin-approved auditions will appear here with YouTube Live links and participation details.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto grid max-w-7xl gap-3 px-4 py-5 sm:grid-cols-3 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 rounded-2xl bg-slate-50 p-4">
            <ShieldCheck className="h-6 w-6 text-emerald-600" />
            <div>
              <p className="font-black">Admin reviewed</p>
              <p className="text-sm text-slate-600">Every collaborator audition is checked before publishing.</p>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-2xl bg-slate-50 p-4">
            <Play className="h-6 w-6 text-[#FF6F00]" />
            <div>
              <p className="font-black">YouTube ready</p>
              <p className="text-sm text-slate-600">Live links, embedded videos, and direct watch buttons.</p>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-2xl bg-slate-50 p-4">
            <Users className="h-6 w-6 text-[#4A148C]" />
            <div>
              <p className="font-black">Built for artists</p>
              <p className="text-sm text-slate-600">Rules, venue, date, host, and art form in one place.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-8 px-4 py-10 sm:px-6 lg:grid-cols-[1fr_420px] lg:px-8">
        <div>
          {artistLoggedIn && artistInvites.length > 0 && (
            <section className="mb-8 rounded-[2rem] border border-emerald-100 bg-white p-5 shadow-sm sm:p-6">
              <p className="text-xs font-extrabold uppercase tracking-[0.24em] text-emerald-700">My Audition Invites</p>
              <h2 className="mt-2 text-2xl font-black text-slate-950">Invitations for {artist?.name || 'you'}</h2>
              <div className="mt-4 grid gap-3">
                {artistInvites.map((invite) => (
                  <div key={invite.id || `${invite.audition_id}-${invite.artist_id}`} className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="font-black text-slate-950">{invite.audition_title || invite.audition?.title || 'Live audition'}</p>
                        <p className="mt-1 text-sm font-semibold text-slate-600">{(invite.status || 'invited').replaceAll('_', ' ')}</p>
                      </div>
                      {(invite.status || 'invited') === 'invited' && (
                        <div className="flex flex-wrap gap-2">
                          <button type="button" onClick={() => respondToInvite(invite, 'accept')} className="rounded-full bg-emerald-600 px-5 py-2 text-sm font-extrabold text-white">Accept</button>
                          <button type="button" onClick={() => respondToInvite(invite, 'decline')} className="rounded-full border border-slate-200 px-5 py-2 text-sm font-extrabold text-slate-700">Decline</button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <div className="flex flex-wrap gap-3 rounded-[1.5rem] border border-slate-200 bg-white p-2 shadow-sm">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-extrabold transition sm:flex-none sm:justify-start ${
                    isActive
                      ? 'bg-[#FF6F00] text-white shadow-lg shadow-orange-200'
                      : 'text-slate-700 hover:bg-orange-50 hover:text-[#C45A00]'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                  <span className={`rounded-full px-2 py-0.5 text-xs ${isActive ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'}`}>
                    {grouped[tab.id]?.length || 0}
                  </span>
                </button>
              );
            })}
          </div>

          {loading ? (
            <div className="mt-8 rounded-[2rem] border border-slate-200 bg-white p-8 text-slate-600 shadow-sm">Loading auditions...</div>
          ) : visibleAuditions.length === 0 ? (
            <div className="mt-8 rounded-[2rem] border border-dashed border-orange-200 bg-white p-8 text-center shadow-sm sm:p-12">
              <p className="text-sm font-extrabold uppercase tracking-[0.32em] text-[#FF6F00]">Coming Soon</p>
              <h2 className="mt-4 text-3xl font-black text-slate-950">No auditions in this section yet.</h2>
              <p className="mx-auto mt-3 max-w-xl text-slate-600">Live auditions will appear here after admin publishes them.</p>
            </div>
          ) : (
            <div className="mt-8 grid gap-5 xl:grid-cols-2">
              {visibleAuditions.map((audition) => (
                <article key={audition.id} className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl">
                  <AuditionMedia audition={audition} />
                  <div className="p-5 sm:p-6">
                    <div className="flex flex-wrap gap-2">
                      <span className={`rounded-full px-3 py-1 text-xs font-extrabold uppercase tracking-[0.12em] ring-1 ${statusStyles[audition.status] || statusStyles.upcoming}`}>
                        {audition.status || 'upcoming'}
                      </span>
                      {audition.art_form && <span className="rounded-full bg-purple-50 px-3 py-1 text-xs font-bold text-[#4A148C]">{audition.art_form}</span>}
                    </div>
                    <h2 className="mt-4 text-2xl font-black leading-tight text-slate-950">{audition.title}</h2>
                    <div className="mt-3 space-y-2 text-sm font-semibold text-slate-600">
                      {audition.audition_date && (
                        <p className="flex gap-2">
                          <CalendarDays className="h-4 w-4 shrink-0 text-[#FF6F00]" />
                          {audition.audition_date}
                        </p>
                      )}
                      <p className="flex gap-2">
                        <MapPin className="h-4 w-4 shrink-0 text-[#FF6F00]" />
                        {metaLine(audition)}
                      </p>
                      {audition.posted_by_name && (
                        <p className="flex gap-2">
                          <Users className="h-4 w-4 shrink-0 text-[#FF6F00]" />
                          Hosted by {audition.posted_by_name}
                        </p>
                      )}
                    </div>
                    {audition.description && <p className="mt-4 text-sm leading-6 text-slate-700">{audition.description}</p>}
                    {audition.rules && <p className="mt-4 rounded-2xl bg-orange-50 px-4 py-3 text-sm font-semibold text-[#9A3412]">{audition.rules}</p>}
                    {Array.isArray(audition.participants) && audition.participants.length > 0 && (
                      <div className="mt-5 rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
                        <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-emerald-700">Selected Participants</p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {audition.participants.map((participant) => (
                            <span key={participant.id || participant.artist_id} className="rounded-full bg-white px-3 py-2 text-xs font-extrabold text-slate-800">
                              {participant.artist_name || 'Selected artist'}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {audition.youtube_url && (
                      <a href={audition.youtube_url} target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#FF6F00] px-5 py-3 text-sm font-extrabold text-white shadow-lg shadow-orange-100">
                        <Play className="h-4 w-4" />
                        Open on YouTube
                      </a>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>

        <aside className="h-fit rounded-[2rem] border border-orange-100 bg-white p-5 shadow-sm sm:p-6 lg:sticky lg:top-28">
          <div className="rounded-[1.5rem] bg-[#07111F] p-5 text-white">
            <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.22em] text-[#FFB347]">
              <Sparkles className="h-4 w-4" />
              Collaborator Desk
            </p>
            <h2 className="mt-3 text-2xl font-black">Post an audition</h2>
            <p className="mt-2 text-sm leading-6 text-slate-200">
              {collaboratorLoggedIn ? `Posting as ${collaborator?.organization_name || collaborator?.name || 'Collaborator'}.` : 'Login as a collaborator to submit a live audition for admin review.'}
            </p>
          </div>

          <form onSubmit={submitAudition} className="mt-5 grid gap-3">
            <label className="text-xs font-extrabold uppercase tracking-[0.18em] text-slate-500">Audition title</label>
            <input value={form.title} onChange={(event) => updateField('title', event.target.value)} placeholder="Sufi night vocal audition" className="rounded-2xl border border-slate-200 px-4 py-3 text-slate-950 outline-none transition focus:border-[#FF6F00] focus:ring-4 focus:ring-orange-100" required disabled={!collaboratorLoggedIn} />

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
              <input value={form.art_form} onChange={(event) => updateField('art_form', event.target.value)} placeholder="Art form" className="rounded-2xl border border-slate-200 px-4 py-3 text-slate-950 outline-none transition focus:border-[#FF6F00] focus:ring-4 focus:ring-orange-100" disabled={!collaboratorLoggedIn} />
              <input value={form.audition_date} onChange={(event) => updateField('audition_date', event.target.value)} placeholder="Date/time" className="rounded-2xl border border-slate-200 px-4 py-3 text-slate-950 outline-none transition focus:border-[#FF6F00] focus:ring-4 focus:ring-orange-100" disabled={!collaboratorLoggedIn} />
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
              <input value={form.city} onChange={(event) => updateField('city', event.target.value)} placeholder="City / online" className="rounded-2xl border border-slate-200 px-4 py-3 text-slate-950 outline-none transition focus:border-[#FF6F00] focus:ring-4 focus:ring-orange-100" disabled={!collaboratorLoggedIn} />
              <input value={form.venue} onChange={(event) => updateField('venue', event.target.value)} placeholder="Venue / channel" className="rounded-2xl border border-slate-200 px-4 py-3 text-slate-950 outline-none transition focus:border-[#FF6F00] focus:ring-4 focus:ring-orange-100" disabled={!collaboratorLoggedIn} />
            </div>

            <input value={form.youtube_url} onChange={(event) => updateField('youtube_url', event.target.value)} placeholder="YouTube Live URL" className="rounded-2xl border border-slate-200 px-4 py-3 text-slate-950 outline-none transition focus:border-[#FF6F00] focus:ring-4 focus:ring-orange-100" disabled={!collaboratorLoggedIn} />
            <p className="rounded-2xl border border-orange-100 bg-orange-50 px-4 py-3 text-xs font-semibold leading-5 text-[#9A3412]">
              If you paste your own YouTube Live URL, the audition runs on your channel. If you leave it blank, KalaManch admin may create the live audition on the official KalaManch YouTube channel after approval. Paste only a public or unlisted YouTube Live URL; private or unreviewable links are rejected.
            </p>
            <textarea value={form.description} onChange={(event) => updateField('description', event.target.value)} placeholder="Audition details, prize, role, selection process" className="min-h-[110px] rounded-2xl border border-slate-200 px-4 py-3 text-slate-950 outline-none transition focus:border-[#FF6F00] focus:ring-4 focus:ring-orange-100" disabled={!collaboratorLoggedIn} />
            <textarea value={form.rules} onChange={(event) => updateField('rules', event.target.value)} placeholder="Rules / participation instructions" className="min-h-[90px] rounded-2xl border border-slate-200 px-4 py-3 text-slate-950 outline-none transition focus:border-[#FF6F00] focus:ring-4 focus:ring-orange-100" disabled={!collaboratorLoggedIn} />

            <button type="submit" disabled={!collaboratorLoggedIn || saving} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FF6F00] px-6 py-3 text-sm font-extrabold text-white shadow-lg shadow-orange-100 transition hover:bg-[#E86200] disabled:bg-slate-300">
              {saving ? <Clock3 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {saving ? 'Submitting...' : 'Submit for Admin Review'}
            </button>
          </form>

          {message && (
            <p className="mt-4 flex gap-2 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-semibold text-slate-700">
              <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
              {message}
            </p>
          )}

          {collaboratorLoggedIn && posterInvites.length > 0 && (
            <section className="mt-5 rounded-[1.5rem] border border-purple-100 bg-purple-50 p-4">
              <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-[#4A148C]">Participant Review</p>
              <h3 className="mt-2 text-lg font-black text-slate-950">Artists invited by you</h3>
              <div className="mt-3 space-y-3">
                {posterInvites.slice(0, 8).map((invite) => (
                  <div key={invite.id || `${invite.audition_id}-${invite.artist_id}`} className="rounded-2xl bg-white p-3 ring-1 ring-purple-100">
                    <p className="font-black text-slate-950">{invite.artist_name || 'Artist'}</p>
                    <p className="mt-1 text-xs font-semibold text-slate-500">{invite.audition_title || invite.audition?.title || 'Live audition'}</p>
                    <select
                      value={invite.status || 'invited'}
                      onChange={(event) => updatePosterInvite(invite, event.target.value)}
                      className="mt-3 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-bold text-slate-900"
                    >
                      <option value="invited">Invited</option>
                      <option value="accepted_invite">Accepted Invite</option>
                      <option value="audition_given">Audition Given</option>
                      <option value="in_review">In Review</option>
                      <option value="selected">Selected</option>
                      <option value="rejected">Rejected</option>
                    </select>
                    {(invite.status || '') === 'selected' && (
                      <button
                        type="button"
                        onClick={() => togglePosterPublic(invite)}
                        className="mt-2 w-full rounded-full border border-emerald-200 px-3 py-2 text-xs font-extrabold text-emerald-700"
                      >
                        {invite.public_visible ? 'Published on public page' : 'Show selected artist publicly'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}
        </aside>
      </section>
    </main>
  );
};

export default LiveAuditions;
